import React, { forwardRef, useMemo, useRef, useImperativeHandle } from 'react';
import s from './s.module.less';
import FormCard from 'pages/NewUniProfile/components/FormCard';
import MalpracticeInsuranceCertificate from 'pages/NewUniProfile/components/LicenseStep/components/MalpracticeInsuranceCertificate';
import DEALicense from 'pages/NewUniProfile/components/LicenseStep/components/DeaLicense';
import CollaboratingPhysician from 'pages/NewUniProfile/components/LicenseStep/components/CollaboratingPhysician';
import AdditionalLicense from 'pages/NewUniProfile/components/LicenseStep/components/AdditionalLicense';
import ProfessionalProfile from 'pages/NewUniProfile/components/ProfessionalProfileStep/components/ProfessionalProfile';
import AdditionalMedia from 'pages/NewUniProfile/components/ProfessionalProfileStep/components/AdditionalMedia';
import Healthcare from 'pages/NewUniProfile/components/BackgroundStep/components/Healthcare';
import Education from 'pages/NewUniProfile/components/BackgroundStep/components/Education';
import Treatment from 'pages/NewUniProfile/components/BackgroundStep/components/Treatment';
import DemographicDetails from 'pages/NewUniProfile/components/BackgroundStep/components/DemographicDetails';
import Recognition from 'pages/NewUniProfile/components/BackgroundStep/components/Recognition';
import BasicInfo from '../BasicInfo';
import { useLicenseInfo } from 'hooks/newUniprofile/useLicenseInfo';
import { useProfessionalProfile } from 'hooks/newUniprofile/useProfessionalProfile';
import { useBackgroundInfo } from 'hooks/newUniprofile/useBackgroundInfo';
import { useBasicInfo } from 'hooks/newUniprofile/useBasicInfo';



const Profile = forwardRef(({ isAva }: { isAva: boolean }, ref) => {
    const [basicInfo, _, fetchBasicInfoData] = useBasicInfo(isAva);
    const [licenseInfo, __, fetchLicenseData] = useLicenseInfo(isAva);
    const [professionalProfile, ___, fetchProfessionalProfileData] = useProfessionalProfile(isAva);
    const [background, loading, refetch, updateBackgroundInfo] = useBackgroundInfo(isAva);
    const basicInfoRef = useRef<any>(null);
    const professionalProfileRef = useRef<any>(null);

    const hasAdditionalMedia = Boolean(professionalProfile?.additionalPhotoList?.length || professionalProfile?.videoList?.length || professionalProfile?.foreignLanguage);
    const hasAdditionalLicense = !!licenseInfo?.additionalLicensesNCertificates?.controlledSubstanceCertification?.length ||
        !!licenseInfo?.additionalLicensesNCertificates?.continuingMedicalEducationCertificates?.length ||
        !!licenseInfo?.additionalLicensesNCertificates?.autonomy?.url ||
        !!licenseInfo?.additionalLicensesNCertificates?.other?.url;
    const hasContentForBiographic = useMemo(() => {
        return Object.values(background.biographic).some((value) => Boolean(value));
    }, [background]);

    const hasContentForRecognition = useMemo(() => {
        return (
            Boolean(background.awardRecognitionList?.length) ||
            Boolean(background.associationRecognitionList?.length) ||
            Boolean(background.publicationRecognitionList?.length)
        );
    }, [background]);

    const onSubmit = async () => {
        try {
            await Promise.all([
                professionalProfileRef.current?.onValidate(),
                basicInfoRef.current?.onValidate(),
            ]);

            // 在 next 时只需要保存 profile 和 basicInfo 即可
            if (professionalProfileRef.current) {
                await professionalProfileRef.current.onSubmit();
            }
            if (basicInfoRef.current) {
                await basicInfoRef.current.onSubmit();
            }
        } catch (error) {
            // 校验失败时滚动定位到第一个错误项，提示用户
            setTimeout(() => {
                const errorEl = document.querySelector('.ant-form-item-has-error');
                errorEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
            console.error(error);
            throw error;
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit,
    }));


    return <div className={s.wrap}>
        <div className={s.title}>Enrich your profile to boost patient trust</div>
        <div className={s.desc}>Complete your information to highlight your expertise and attract more patients with confidence.</div>
        <BasicInfo initValue={basicInfo} refetch={fetchBasicInfoData} ref={basicInfoRef} />
        <FormCard type='edit' ref={professionalProfileRef}>
            {(isEditing) => <ProfessionalProfile isEditing={isEditing} initValue={professionalProfile} refetch={fetchProfessionalProfileData} showTitle />}
        </FormCard>
        <FormCard type={hasAdditionalMedia ? 'review' : 'add'} title="Additional Media" isOptional hasContent={hasAdditionalMedia} forceEdit={hasAdditionalMedia && isAva} >
            {(isEditing) => <AdditionalMedia isEditing={isEditing} initValue={professionalProfile} refetch={fetchProfessionalProfileData} />}
        </FormCard>
        <FormCard type={Boolean(background.providerExperienceList?.length) ? 'review' : 'add'} title="Healthcare experience" isOptional hasContent={Boolean(background.providerExperienceList?.length)} forceEdit={Boolean(background.providerExperienceList?.length) && isAva}>
            {(isEditing) => <Healthcare isEditing={isEditing} initValue={background.providerExperienceList} updateFn={updateBackgroundInfo} />}
        </FormCard>
        <FormCard type={Boolean(background.providerEducationList?.length) ? 'review' : 'add'} title="Education" isOptional hasContent={Boolean(background.providerEducationList?.length)} forceEdit={Boolean(background.providerEducationList?.length) && isAva}>
            {(isEditing) => <Education isEditing={isEditing} initValue={background.providerEducationList} updateFn={updateBackgroundInfo} />}
        </FormCard>
        <FormCard type={Boolean(background.treatmentPhilosophy) ? 'review' : 'add'} title="Treatment methodology" isOptional hasContent={Boolean(background.treatmentPhilosophy)} forceEdit={Boolean(background.treatmentPhilosophy) && isAva}>
            {(isEditing) => <Treatment isEditing={isEditing} initValue={background.treatmentPhilosophy} conversationId={background.philosophyConversationId} updateFn={updateBackgroundInfo} />}
        </FormCard>
        <FormCard type={hasContentForBiographic ? 'review' : 'add'} title="Demographic details" isOptional hasContent={hasContentForBiographic} forceEdit={hasContentForBiographic && isAva}>
            {(isEditing) => <DemographicDetails isEditing={isEditing} initValue={background.biographic} hasContent={hasContentForBiographic} updateFn={updateBackgroundInfo} />}
        </FormCard>
        <FormCard type={hasContentForRecognition ? 'review' : 'add'} title="Recognition" isOptional hasContent={hasContentForRecognition} forceEdit={hasContentForRecognition && isAva}>
            {(isEditing) => <Recognition isEditing={isEditing} awardRecognitionList={background.awardRecognitionList} associationRecognitionList={background.associationRecognitionList} publicationRecognitionList={background.publicationRecognitionList} updateFn={updateBackgroundInfo} />}
        </FormCard>
        <FormCard type={!!licenseInfo?.malpracticeInsuranceCertificate?.licenseNumber ? 'review' : 'add'} title="Malpractice insurance certificate" isOptional hasContent={!!licenseInfo?.malpracticeInsuranceCertificate?.licenseNumber} forceEdit={!!licenseInfo?.malpracticeInsuranceCertificate?.licenseNumber && isAva}>
            {(isEditing) => <MalpracticeInsuranceCertificate isEditing={isEditing} initValue={licenseInfo?.malpracticeInsuranceCertificate} refetch={fetchLicenseData} />}
        </FormCard>
        <FormCard type={!!licenseInfo?.deaLicense?.length ? 'review' : 'add'} title="DEA licenses" isOptional hasContent={!!licenseInfo?.deaLicense?.length} forceEdit={!!licenseInfo?.deaLicense?.length && isAva}>
            {(isEditing) => <DEALicense isEditing={isEditing} initValue={licenseInfo?.deaLicense} refetch={fetchLicenseData} />}
        </FormCard>
        <FormCard type={!!licenseInfo?.collaboratingPhysician?.length ? 'review' : 'add'} title="Collaborating physician" isOptional hasContent={!!licenseInfo?.collaboratingPhysician?.length} forceEdit={!!licenseInfo?.collaboratingPhysician?.length && isAva}>
            {(isEditing) => <CollaboratingPhysician isEditing={isEditing} initValue={licenseInfo?.collaboratingPhysician} refresh={fetchLicenseData} />}
        </FormCard>
        <FormCard type={hasAdditionalLicense ? 'review' : 'add'} title="Additional licences and certificates" isOptional hasContent={hasAdditionalLicense} forceEdit={hasAdditionalLicense && isAva}>
            {(isEditing) => (
                <AdditionalLicense
                    isEditing={isEditing}
                    initValue={licenseInfo?.additionalLicensesNCertificates}
                    refetch={fetchLicenseData}
                />
            )}
        </FormCard>
    </div>;
});

export default Profile;